import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldWarning } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function SettingsPage() {
  const { org, refreshOrg, user } = useApp();
  const [team, setTeam] = useState([]);
  const [audit, setAudit] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "", role: "operator", password: "" });
  const [orgName, setOrgName] = useState("");
  const [twofaSetup, setTwofaSetup] = useState(null);
  const [twofaCode, setTwofaCode] = useState("");
  const [twofaPassword, setTwofaPassword] = useState("");

  const load = useCallback(() => {
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/audit").then((r) => setAudit(r.data.slice(0, 15)));
  }, []);
  useEffect(load, [load]);
  useEffect(() => { setOrgName(org?.name || ""); }, [org]);

  const saveOrg = async () => {
    await api.put("/org", { name: orgName });
    refreshOrg();
    toast.success("Workspace updated");
  };

  const toggleKill = async (enabled) => {
    await api.put("/org", { ai_enabled: enabled });
    refreshOrg();
    toast[enabled ? "success" : "warning"](enabled ? "AI re-enabled" : "Global kill switch active — all AI generation blocked");
  };

  const toggleAutoInsights = async (enabled) => {
    await api.put("/org", { auto_insights: enabled });
    refreshOrg();
    toast.success(enabled ? "Weekly AI insights auto-refresh enabled (runs Mondays, 1 credit per client)" : "Auto-refresh disabled");
  };

  const startTwofa = async () => {
    const { data } = await api.post("/auth/2fa/setup");
    setTwofaSetup(data);
    setTwofaCode("");
  };

  const enableTwofa = async () => {
    try {
      await api.post("/auth/2fa/enable", { code: twofaCode });
      setTwofaSetup(null); setTwofaCode("");
      refreshOrg();
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const disableTwofa = async () => {
    try {
      await api.post("/auth/2fa/disable", { code: twofaCode, password: twofaPassword });
      setTwofaCode(""); setTwofaPassword("");
      refreshOrg();
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const sendInvite = async () => {
    if (!invite.email || !invite.name) return toast.error("Name and email required");
    try {
      await api.post("/team/invite", invite);
      setInvite({ email: "", name: "", role: "operator", password: "" });
      load();
      toast.success("Team member added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
  };

  return (
    <div className="space-y-6 fade-up max-w-3xl" data-testid="settings-page">
      <h1 className="font-display font-bold text-3xl tracking-tight">Settings</h1>

      <div className="bg-white border border-slate-200/80 rounded-xl p-6 card-float space-y-4">
        <h2 className="font-display font-bold text-base">Workspace</h2>
        <div className="flex gap-3">
          <Input data-testid="org-name-input" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="max-w-xs" />
          <Button data-testid="org-save-button" variant="outline" className="rounded-lg" onClick={saveOrg}>Save</Button>
        </div>
        <div className="flex items-center justify-between border border-orange-200 bg-orange-50/60 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <ShieldWarning size={20} weight="duotone" className="text-orange-500 mt-0.5" />
            <div>
              <p className="text-[13px] font-semibold">Global AI kill switch</p>
              <p className="text-[12px] text-slate-500">Instantly blocks all AI generation across every client.</p>
            </div>
          </div>
          <Switch data-testid="kill-switch-toggle" checked={org?.ai_enabled ?? true} onCheckedChange={toggleKill} />
        </div>
        <div className="flex items-center justify-between border border-slate-200 rounded-xl p-4">
          <div>
            <p className="text-[13px] font-semibold">Weekly AI insights auto-refresh</p>
            <p className="text-[12px] text-slate-500">Every Monday, regenerate strategy insights for each client with performance data (1 credit each).</p>
          </div>
          <Switch data-testid="auto-insights-toggle" checked={org?.auto_insights ?? false} onCheckedChange={toggleAutoInsights} />
        </div>
        <div className="border border-slate-200 rounded-xl p-4">
          <p className="text-[13px] font-semibold mb-3">Integrations <span className="text-[11px] font-normal text-slate-400">— mock-first, flip to live when keys are added</span></p>
          <div className="space-y-2">
            {[["SEMrush / Socialinsider research", "research"], ["Brand24 / Mention monitoring", "monitoring"], ["fal.ai video generation (Veo/Runway/Kling)", "video"]].map(([label, key]) => (
              <div key={key} className="flex items-center justify-between text-[12px]" data-testid={`integration-row-${key}`}>
                <span className="text-slate-600">{label}</span>
                <button onClick={() => toast.info("Add the provider API key to enable live mode — mock fixtures active")}
                  className="px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold uppercase tracking-widest hover:bg-amber-100 transition-colors">Mock</button>
              </div>
            ))}
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-slate-600">Claude text · Nano Banana images · Stripe billing</span>
              <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">Live</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-6 card-float space-y-4">
        <h2 className="font-display font-bold text-base">Security</h2>
        <div className="flex items-center justify-between border border-slate-200 rounded-xl p-4">
          <div>
            <p className="text-[13px] font-semibold">Two-factor authentication (TOTP)</p>
            <p className="text-[12px] text-slate-500">{user?.twofa_enabled ? "Enabled — a 6-digit code is required at login." : "Protect your account with an authenticator app (Google Authenticator, 1Password, Authy)."}</p>
          </div>
          {user?.twofa_enabled ? (
            <div className="flex gap-2 items-center">
              <Input data-testid="twofa-disable-password" type="password" placeholder="Password" value={twofaPassword} onChange={(e) => setTwofaPassword(e.target.value)} className="w-28 h-8 text-[13px]" />
              <Input data-testid="twofa-disable-code" placeholder="Code" value={twofaCode} onChange={(e) => setTwofaCode(e.target.value)} className="w-24 h-8 text-[13px]" />
              <Button data-testid="twofa-disable-button" variant="outline" size="sm" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" onClick={disableTwofa}>Disable</Button>
            </div>
          ) : (
            <Button data-testid="twofa-enable-button" variant="outline" size="sm" className="rounded-lg" onClick={startTwofa}>Enable 2FA</Button>
          )}
        </div>
        <p className="text-[12px] text-slate-400">Social login: Google is live. Microsoft & Apple sign-in are not offered.</p>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl card-float">
        <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-base">Team & roles</h2></div>
        <div className="divide-y divide-slate-100">
          {team.map((m) => (
            <div key={m.user_id} className="px-6 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[12px] font-semibold text-slate-600">{m.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1">
                <p className="text-[13px] font-medium">{m.name} {m.user_id === user?.user_id && <span className="text-slate-400">(you)</span>}</p>
                <p className="text-[12px] text-slate-400">{m.email}</p>
              </div>
              <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">{m.role}</span>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row gap-2">
          <Input data-testid="invite-name-input" placeholder="Name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
          <Input data-testid="invite-email-input" placeholder="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
          <Input data-testid="invite-password-input" type="password" placeholder="Password (optional)" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} />
          <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
            <SelectTrigger data-testid="invite-role-select" className="sm:w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["admin", "operator", "designer", "translator"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="invite-submit-button" onClick={sendInvite} className="rounded-lg bg-slate-900 hover:bg-slate-700 shrink-0">Invite</Button>
        </div>
      </div>

      <Dialog open={!!twofaSetup} onOpenChange={(o) => !o && setTwofaSetup(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Set up two-factor auth</DialogTitle></DialogHeader>
          {twofaSetup && (
            <div className="space-y-4">
              <p className="text-[13px] text-slate-500">1. Scan this QR with your authenticator app:</p>
              <img data-testid="twofa-qr" src={`data:image/png;base64,${twofaSetup.qr_png_base64}`} alt="2FA QR" className="w-44 h-44 mx-auto border border-slate-200 rounded-lg" />
              <p className="text-[11px] text-slate-400 font-mono text-center break-all">or enter manually: {twofaSetup.secret}</p>
              <p className="text-[13px] text-slate-500">2. Enter the 6-digit code it shows:</p>
              <Input data-testid="twofa-enable-code" inputMode="numeric" maxLength={6} placeholder="123456" value={twofaCode} onChange={(e) => setTwofaCode(e.target.value)} />
              <Button data-testid="twofa-confirm-button" onClick={enableTwofa} disabled={twofaCode.length !== 6} className="w-full rounded-lg bg-slate-900 hover:bg-slate-700">Confirm & enable</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-slate-200/80 rounded-xl card-float">
        <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-base">Audit log</h2></div>
        <div className="divide-y divide-slate-100">
          {audit.map((a) => (
            <div key={a.log_id} className="px-6 py-2.5 text-[12px] text-slate-500">
              <b className="text-slate-700">{a.user_name}</b> · {a.action.replace(/_/g, " ")} {a.detail && <span>— {a.detail}</span>}
              <span className="text-slate-300"> · {new Date(a.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
